import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useCompany } from "@/contexts/CompanyContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ClipboardList, Printer, Building2, Sparkles, Users2, RefreshCw, Filter,
  FileDown, Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";

const TURNO_LABEL = {
  ADMINISTRATIVO: "Administrativo",
  A: "Turno A", B: "Turno B", C: "Turno C", D: "Turno D",
};
const TONE_LABEL = {
  completo: { txt: "Completo", cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  excedido: { txt: "Acima do aprovado", cls: "bg-sky-100 text-sky-700 border-sky-200" },
  atencao:  { txt: "Atenção", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  deficit:  { txt: "Abaixo do aprovado", cls: "bg-rose-100 text-rose-700 border-rose-200" },
  neutral:  { txt: "Sem meta", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

export default function WorkBoardReportPage() {
  const { companyParam, companies, selected, select } = useCompany();
  const [sectors, setSectors] = useState([]);
  const [sectorId, setSectorId] = useState("");
  const [subSetor, setSubSetor] = useState("all");
  const [turno, setTurno] = useState("all");
  const [cargo, setCargo] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingSectors, setLoadingSectors] = useState(true);
  const [orientation, setOrientation] = useState("landscape");
  const [pageSize, setPageSize] = useState("A4");

  // Load sectors filtered by selected company
  useEffect(() => {
    (async () => {
      setLoadingSectors(true);
      try {
        const params = {};
        if (companyParam) params.company_id = companyParam;
        const { data } = await api.get("/sectors", { params });
        setSectors(data);
        if (data.length && !sectorId) setSectorId(data[0].id);
        else if (!data.find((s) => s.id === sectorId)) setSectorId(data[0]?.id || "");
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally { setLoadingSectors(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyParam]);

  const generate = async () => {
    if (!sectorId) {
      toast.error("Selecione um setor");
      return;
    }
    setLoading(true);
    try {
      const params = { sector_id: sectorId };
      if (subSetor !== "all") params.sub_setor = subSetor;
      if (turno !== "all") params.turno = turno;
      if (cargo.trim()) params.cargo = cargo.trim();
      const { data } = await api.get("/reports/work-board-by-sector", { params });
      setReport(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  // Auto-generate on mount + on sector change
  useEffect(() => {
    if (sectorId) generate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectorId]);

  const handlePrint = () => {
    document.documentElement.setAttribute("data-print-orient", orientation);
    document.documentElement.setAttribute("data-print-size", pageSize);
    const area = document.getElementById("wb-rep-print");
    if (area) {
      const rect = area.getBoundingClientRect();
      const sizes = {
        A4: { landscape: [1080, 744], portrait: [744, 1080] },
        A3: { landscape: [1530, 1056], portrait: [1056, 1530] },
      };
      const [targetW, targetH] = sizes[pageSize][orientation];
      const fit = Math.min(targetW / rect.width, targetH / rect.height, 1);
      document.documentElement.style.setProperty("--wb-print-fit", fit.toFixed(3));
    }
    setTimeout(() => window.print(), 80);
  };

  const exportPNG = async () => {
    const area = document.getElementById("wb-rep-print");
    if (!area) return;
    try {
      const { default: html2canvas } = await import("html2canvas");
      const canvas = await html2canvas(area, { backgroundColor: "#ffffff", scale: 2 });
      const link = document.createElement("a");
      link.download = `quadro-${report?.sector?.name || "setor"}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Imagem PNG exportada");
    } catch (e) {
      toast.error("Falha ao exportar imagem");
    }
  };

  const exportPDF = async () => {
    const area = document.getElementById("wb-rep-print");
    if (!area) return;
    area.classList.add("wb-pdf-capturing");
    await new Promise((r) => setTimeout(r, 60));
    try {
      const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
        import("html2canvas"), import("jspdf"),
      ]);
      const isLandscape = orientation === "landscape";
      // Larguras fixas conhecidas (equivalentes ao ternário aninhado anterior).
      const windowW = (() => {
        if (pageSize === "A3") return isLandscape ? 1580 : 1120;
        return isLandscape ? 1120 : 794;
      })();
      const canvas = await html2canvas(area, {
        backgroundColor: "#ffffff",
        scale: 2,
        useCORS: true,
        windowWidth: windowW,
        width: area.scrollWidth,
      });
      const pdf = new jsPDF({
        orientation: isLandscape ? "l" : "p",
        unit: "mm",
        format: pageSize.toLowerCase(),
      });
      const MARGIN_X = 6;
      const HEADER_Y = 5;
      const HEADER_H = 8;
      const FOOTER_H = 6;
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const usableW = pageW - MARGIN_X * 2;
      const usableH = pageH - HEADER_H - HEADER_Y - FOOTER_H;

      // Fator de escala mm/px baseado na largura útil
      const mmPerPx = usableW / canvas.width;
      // Altura em px que representa uma página inteira (máximo)
      const maxPxPerPage = Math.floor(usableH / mmPerPx);
      const totalPx = canvas.height;
      const totalPages = Math.max(1, Math.ceil(totalPx / maxPxPerPage));
      // Rebalanceia: divide igualmente entre as páginas para não sobrar meia página vazia
      const balancedPxPerPage = Math.ceil(totalPx / totalPages);

      const drawHeader = (pageNum) => {
        pdf.setFontSize(9);
        pdf.setTextColor(35, 73, 53);
        pdf.text(`Quadro de Trabalho — ${report?.sector?.name || ""}`, MARGIN_X, HEADER_Y + 3);
        pdf.setFontSize(6.5);
        pdf.setTextColor(120, 120, 120);
        const stamp = `Emitido em ${new Date().toLocaleString("pt-BR")} · ${pageSize} ${isLandscape ? "paisagem" : "retrato"}`;
        pdf.text(stamp, MARGIN_X, HEADER_Y + 7);
        pdf.setFontSize(6);
        pdf.setTextColor(140, 140, 140);
        pdf.text(`TheoKratos RH · página ${pageNum} de ${totalPages}`, MARGIN_X, pageH - 2);
      };

      // Slice o canvas em faixas verticais, uma por página (balanceadas)
      for (let p = 0; p < totalPages; p++) {
        if (p > 0) pdf.addPage();
        const sliceStartPx = p * balancedPxPerPage;
        const sliceHeightPx = Math.min(balancedPxPerPage, totalPx - sliceStartPx);
        // Cria um canvas parcial para essa faixa
        const sliceCanvas = document.createElement("canvas");
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = sliceHeightPx;
        const ctx = sliceCanvas.getContext("2d");
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
        ctx.drawImage(
          canvas,
          0, sliceStartPx, canvas.width, sliceHeightPx,
          0, 0, canvas.width, sliceHeightPx,
        );
        const sliceImg = sliceCanvas.toDataURL("image/png");
        const wMm = usableW;
        const hMm = sliceHeightPx * mmPerPx;
        drawHeader(p + 1);
        pdf.addImage(sliceImg, "PNG", MARGIN_X, HEADER_Y + HEADER_H, wMm, hMm, undefined, "FAST");
      }

      pdf.save(`quadro-${(report?.sector?.name || "setor").replace(/\s+/g, "_")}-${pageSize}.pdf`);
      toast.success(`PDF ${pageSize} ${isLandscape ? "paisagem" : "retrato"} exportado (${totalPages} ${totalPages === 1 ? "página" : "páginas"})`);
    } catch (e) {
      toast.error("Falha ao exportar PDF");
    } finally {
      area.classList.remove("wb-pdf-capturing");
    }
  };

  const exportXlsx = async () => {
    try {
      const params = { sector_id: sectorId };
      if (subSetor !== "all") params.sub_setor = subSetor;
      if (turno !== "all") params.turno = turno;
      if (cargo.trim()) params.cargo = cargo.trim();
      const r = await api.get("/reports/work-board-by-sector/export.xlsx", { params, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `quadro-${(report?.sector?.name || "setor").replace(/\s+/g, "_")}.xlsx`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel exportado");
    } catch (e) {
      toast.error("Falha ao exportar Excel");
    }
  };

  const groupedByCargo = useMemo(() => {
    if (!report) return {};
    const g = {};
    for (const e of report.employees) {
      const k = e.cargo || "—";
      if (!g[k]) g[k] = [];
      g[k].push(e);
    }
    return g;
  }, [report]);

  return (
    <div className="space-y-5" data-testid="wb-report-page">
      {/* Hero — hidden on print */}
      <div className="relative overflow-hidden rounded-2xl border border-[#C5A059]/25 bg-gradient-to-br from-[#234935] via-[#1C3A2B] to-[#0F1C14] p-6 text-white print:hidden">
        <div aria-hidden className="absolute -top-24 -right-24 h-64 w-64 rounded-full bg-[#C5A059]/20 blur-[80px]" />
        <div className="relative">
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C5A059] font-medium flex items-center gap-1.5">
            <ClipboardList className="h-3 w-3" /> Relatório
          </div>
          <h1 className="mt-1 font-display text-3xl md:text-[34px] font-semibold tracking-tight">
            Quadro de Trabalho por Setor
          </h1>
          <p className="mt-2 text-[13px] text-white/70 max-w-[680px]">
            Relatório executivo com colaboradores agrupados por sub-setor e turno, e indicadores de
            aderência ao quadro aprovado. Otimizado para impressão em A4 paisagem.
          </p>
        </div>
      </div>

      {/* Filters — hidden on print */}
      <div className="rounded-xl border border-border bg-white p-4 print:hidden" data-testid="wb-filters">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Empresa</Label>
            <Select value={selected || "all"} onValueChange={(v) => select(v)}>
              <SelectTrigger data-testid="wb-rep-company"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas as empresas</SelectItem>
                {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.nome_fantasia || c.razao_social}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Setor</Label>
            <Select value={sectorId} onValueChange={setSectorId}>
              <SelectTrigger data-testid="wb-rep-sector">
                <SelectValue placeholder={loadingSectors ? "Carregando..." : "Selecione..."} />
              </SelectTrigger>
              <SelectContent>
                {sectors.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Sub-setor</Label>
            <Select value={subSetor} onValueChange={setSubSetor}>
              <SelectTrigger data-testid="wb-rep-sub"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {(report?.filter_options?.sub_setores || []).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Turno</Label>
            <Select value={turno} onValueChange={setTurno}>
              <SelectTrigger data-testid="wb-rep-turno"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {Object.entries(TURNO_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider">Função (contém)</Label>
            <Input
              value={cargo}
              onChange={(e) => setCargo(e.target.value)}
              placeholder="Ex.: operador"
              data-testid="wb-rep-cargo"
              onKeyDown={(e) => e.key === "Enter" && generate()}
            />
          </div>
        </div>
        <div className="mt-3 flex items-center gap-2 justify-end flex-wrap">
          <Select value={pageSize} onValueChange={setPageSize}>
            <SelectTrigger className="w-[76px] h-9" data-testid="wb-page-size"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="A4">A4</SelectItem>
              <SelectItem value="A3">A3</SelectItem>
            </SelectContent>
          </Select>
          <Select value={orientation} onValueChange={setOrientation}>
            <SelectTrigger className="w-[120px] h-9" data-testid="wb-orient"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="landscape">Paisagem</SelectItem>
              <SelectItem value="portrait">Retrato</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={generate} disabled={loading} data-testid="wb-rep-generate">
            <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Gerar / atualizar
          </Button>
          <Button size="sm" variant="outline" onClick={exportXlsx} disabled={!report} data-testid="wb-rep-xlsx">
            <FileDown className="h-3.5 w-3.5 mr-1.5" /> Excel
          </Button>
          <Button size="sm" variant="outline" onClick={exportPDF} disabled={!report} data-testid="wb-rep-pdf">
            <FileDown className="h-3.5 w-3.5 mr-1.5" /> PDF
          </Button>
          <Button size="sm" variant="outline" onClick={exportPNG} disabled={!report} data-testid="wb-rep-png">
            <ImageIcon className="h-3.5 w-3.5 mr-1.5" /> Imagem
          </Button>
          <Button size="sm" onClick={handlePrint} disabled={!report} className="bg-[#C5A059] hover:bg-[#D4B36B] text-[#1A231F]" data-testid="wb-rep-print">
            <Printer className="h-3.5 w-3.5 mr-1.5" /> Imprimir {pageSize} {orientation === "landscape" ? "paisagem" : "retrato"}
          </Button>
        </div>
      </div>

      {/* Report area — 3 estados: loading, vazio, conteúdo */}
      {(() => {
        if (loading) return <Skeleton className="h-[440px] rounded-xl" />;
        if (!report) return (
          <div className="rounded-xl border border-dashed border-border bg-white p-10 text-center text-muted-foreground">
            Selecione um setor para gerar o relatório.
          </div>
        );
        return (
          <div className="bg-white rounded-xl border border-border print:rounded-none print:border-0 print:shadow-none" data-testid="wb-rep-content" id="wb-rep-print">
          {/* Print header */}
          <div className="px-6 pt-6 pb-4 border-b border-border print:px-2 print:pt-0">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#8A6B2B] font-semibold">
                  Quadro de Trabalho por Setor
                </div>
                <div className="mt-1 font-display text-2xl font-semibold tracking-tight print:text-[18px]">
                  {report.sector.name}
                </div>
                <div className="mt-1 text-[12.5px] text-muted-foreground flex flex-wrap gap-x-3 gap-y-0.5">
                  <span><Building2 className="inline h-3 w-3 mr-1" />{report.company.name}</span>
                  {report.sector.responsavel && <span>Responsável: <b>{report.sector.responsavel}</b></span>}
                  {report.sector.code && <span>Código: <b>{report.sector.code}</b></span>}
                </div>
              </div>
              <div className="text-right text-[11px] text-muted-foreground tabular-nums">
                <div>Emitido em <b>{new Date(report.generated_at).toLocaleString("pt-BR")}</b></div>
                <div>Por <b>{report.generated_by || "—"}</b></div>
              </div>
            </div>
          </div>

          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5 px-6 py-4 bg-slate-50/60 print:bg-white print:px-2">
            <Kpi label="Total" value={report.summary.total} accent />
            <Kpi label="Quadro aprovado" value={report.sector.quadro_aprovado} />
            <Kpi label="Diferença" value={`${report.sector.diferenca >= 0 ? "+" : ""}${report.sector.diferenca}`}
                 tone={report.sector.diferenca < 0 ? "rose" : "emerald"} />
            <Kpi label="% Ocupação" value={`${report.sector.ocupacao_pct}%`} />
            <div className="rounded-lg border border-border bg-white p-2.5">
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Status do quadro</div>
              <div className="mt-1">
                <Badge variant="outline" className={TONE_LABEL[report.sector.tone || "neutral"].cls}>
                  {TONE_LABEL[report.sector.tone || "neutral"].txt}
                </Badge>
              </div>
            </div>
          </div>

          {/* Distribution by turno */}
          <div className="px-6 py-3 print:px-2">
            <div className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-2">
              Distribuição por turno
            </div>
            <div className="flex gap-2 flex-wrap text-[12px]">
              {Object.entries(report.summary.by_turno).map(([k, v]) => (
                <span key={k} className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-2.5 py-1">
                  <span className="font-medium">{k === "sem_turno" ? "Sem turno" : (TURNO_LABEL[k] || k)}</span>
                  <span className="font-mono tabular-nums text-slate-700">{v}</span>
                </span>
              ))}
            </div>
          </div>

          {/* Employees table */}
          <div className="px-6 pb-4 print:px-2">
            <div className="rounded-lg border border-border overflow-hidden" data-testid="wb-rep-table">
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="bg-[#234935] text-white">
                    <th className="text-left px-3 py-2 font-semibold w-[28%]">Colaborador</th>
                    <th className="text-left px-3 py-2 font-semibold">Matrícula</th>
                    <th className="text-left px-3 py-2 font-semibold">Função</th>
                    <th className="text-left px-3 py-2 font-semibold">Sub-setor</th>
                    <th className="text-left px-3 py-2 font-semibold">Turno</th>
                    <th className="text-left px-3 py-2 font-semibold">Escala</th>
                    <th className="text-left px-3 py-2 font-semibold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {report.employees.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-8 text-muted-foreground">Nenhum colaborador encontrado para os filtros selecionados.</td></tr>
                  ) : (
                    report.employees.map((e, idx) => (
                      <tr key={e.id} className={idx % 2 ? "bg-slate-50/60 print:bg-white" : "bg-white"}>
                        <td className="px-3 py-1.5 font-medium">{e.nome}</td>
                        <td className="px-3 py-1.5 font-mono tabular-nums text-slate-700">{e.matricula || "—"}</td>
                        <td className="px-3 py-1.5">{e.cargo || "—"}</td>
                        <td className="px-3 py-1.5">{e.sub_setor || <span className="text-slate-400 italic">—</span>}</td>
                        <td className="px-3 py-1.5">{TURNO_LABEL[e.turno_operacional] || <span className="text-slate-400 italic">—</span>}</td>
                        <td className="px-3 py-1.5 capitalize">{e.turno || "—"}</td>
                        <td className="px-3 py-1.5">
                          <Badge variant="outline" className="text-[10px] capitalize">
                            {e.status || "ativo"}
                          </Badge>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* By cargo summary */}
          {report.summary.by_cargo.length > 0 && (
            <div className="px-6 pb-6 print:px-2 print:pb-2">
              <div className="text-[10.5px] uppercase tracking-[0.18em] text-muted-foreground font-semibold mb-2">
                Quantidade por função
              </div>
              <div className="rounded-lg border border-border overflow-hidden">
                <table className="w-full text-[12px]">
                  <thead>
                    <tr className="bg-slate-100">
                      <th className="text-left px-3 py-1.5">Função</th>
                      <th className="text-right px-3 py-1.5 w-[100px]">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.summary.by_cargo.map((c) => (
                      <tr key={c.cargo}>
                        <td className="px-3 py-1.5">{c.cargo}</td>
                        <td className="px-3 py-1.5 text-right font-mono tabular-nums">{c.count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Print-only footer */}
          <div className="hidden print:block text-[9px] text-slate-500 text-center py-2 border-t border-slate-200 mt-2">
            TheoKratos RH · Quadro de Trabalho · Página gerada em {new Date().toLocaleString("pt-BR")}
          </div>
        </div>
        );
      })()}

      <style>{`
        @media print {
          @page { size: ${pageSize} ${orientation}; margin: 8mm; }
          html, body { background: white !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; overflow: visible !important; height: auto !important; }
          aside, [data-sidebar], header, nav, .print\\:hidden { display: none !important; }
          main, body > div { padding: 0 !important; margin: 0 !important; overflow: visible !important; }
          [data-testid="wb-report-page"] { padding: 0 !important; margin: 0 !important; overflow: visible !important; }
          #wb-rep-print {
            transform: scale(var(--wb-print-fit, 1)) !important;
            transform-origin: top left !important;
            overflow: visible !important;
            max-height: none !important;
            page-break-inside: auto;
          }
          /* Ensure no container is clipping the content in print */
          #wb-rep-print * { overflow: visible !important; max-height: none !important; }
          /* Avoid breaking inside individual rows */
          tr { break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}

function Kpi({ label, value, tone, accent }) {
  const getToneClass = () => {
    if (tone === "rose") return "text-rose-600";
    if (tone === "emerald") return "text-emerald-600";
    return "text-slate-800";
  };
  const toneCls = getToneClass();
  return (
    <div className={`rounded-lg border p-2.5 ${accent ? "border-[#234935]/30 bg-[#234935]/[0.05]" : "border-border bg-white"}`}>
      <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">{label}</div>
      <div className={`font-display text-xl font-semibold tabular-nums mt-0.5 print:text-[16px] ${accent ? "text-[#234935]" : toneCls}`}>{value ?? 0}</div>
    </div>
  );
}
